export const formatDateInput = (value: string) => {
  const digits = value.replace(/\D/g, '');

  let day = '';
  let month = '';
  let year = '';
  let result = '';

  if (digits.length >= 1) {
    const firstDigit = digits[0];
    if (digits.length === 1) {
      if (parseInt(firstDigit, 10) >= 4) {
        day = '0' + firstDigit;
      } else {
        day = firstDigit;
      }
    }

    if (digits.length >= 2) {
      day = digits.slice(0, 2);
    }
  }

  if (day.length === 2) {
    const dayNumber = parseInt(day, 10);
    if (!Number.isNaN(dayNumber) && dayNumber > 31) {
      day = '31';
    }
  }

  if (day) {
    result = day;
    if (day.length === 2) {
      result += '.';
    }
  }

  if (digits.length >= 3) {
    const thirdDigit = digits[2];
    if (digits.length === 3) {
      if (parseInt(thirdDigit, 10) >= 2) {
        month = '0' + thirdDigit;
      } else {
        month = thirdDigit;
      }
    }

    if (digits.length >= 4) {
      month = digits.slice(2, 4);
    }
  }

  if (month.length === 2) {
    const monthNumber = parseInt(month, 10);
    if (!Number.isNaN(monthNumber) && monthNumber > 12) {
      month = '12';
    }
  }

  if (month) {
    result += month;
    if (month.length === 2) {
      result += '.';
    }
  }

  if (digits.length >= 5) {
    year = digits.slice(4, 8);
    result += year;
  }

  return result;
};

export const applySmartDateBackspace = (
  value: string,
  caretPos: number,
): { value: string; caretPos: number; handled: boolean } => {
  if (caretPos <= 0) return { value, caretPos, handled: false };
  if (value[caretPos - 1] !== '.') return { value, caretPos, handled: false };

  const start = Math.max(0, caretPos - 2);
  const end = caretPos;
  let nextValue = value.slice(0, start) + value.slice(end);
  let nextCaretPos = start;

  if (
    nextCaretPos > 0 &&
    nextValue[nextCaretPos - 1] === '0' &&
    (nextCaretPos - 2 < 0 || nextValue[nextCaretPos - 2] === '.')
  ) {
    nextValue = nextValue.slice(0, nextCaretPos - 1) + nextValue.slice(nextCaretPos);
    nextCaretPos -= 1;
  }

  return { value: nextValue, caretPos: nextCaretPos, handled: true };
};

export const applySmartDateDot = (
  value: string,
  caretPos: number,
): { value: string; caretPos: number; handled: boolean } => {
  if (caretPos <= 0) return { value, caretPos, handled: true };

  const prevChar = value[caretPos - 1];

  if (prevChar === '.') return { value, caretPos, handled: true };

  if (!/\d/.test(prevChar)) return { value, caretPos, handled: true };

  const segmentStart = value.lastIndexOf('.', caretPos - 1) + 1;
  const segment = value.slice(segmentStart, caretPos);

  if (segment.length === 0) return { value, caretPos, handled: true };

  let nextValue = value;
  let nextCaretPos = caretPos;

  if (segment.length === 1) {
    nextValue = value.slice(0, segmentStart) + '0' + segment + value.slice(caretPos);
    nextCaretPos = caretPos + 1;
  }

  if (nextValue[nextCaretPos] !== '.') {
    nextValue = nextValue.slice(0, nextCaretPos) + '.' + nextValue.slice(nextCaretPos);
  }

  return { value: nextValue, caretPos: nextCaretPos + 1, handled: true };
};

export const applySmartDateKey = (params: {
  key: string;
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
}): { value: string; caretPos: number; handled: boolean } => {
  const { key, value, selectionStart, selectionEnd } = params;

  if (selectionStart == null || selectionEnd == null) {
    return { value, caretPos: 0, handled: false };
  }
  if (selectionStart !== selectionEnd) {
    return { value, caretPos: selectionStart, handled: false };
  }

  const caretPos = selectionStart;

  if (key === '.') {
    const dotResult = applySmartDateDot(value, caretPos);
    const formatted = formatDateInput(dotResult.value);
    return { value: formatted, caretPos: dotResult.caretPos, handled: true };
  }

  if (key === 'Backspace') {
    const backspaceResult = applySmartDateBackspace(value, caretPos);
    if (!backspaceResult.handled) return { value, caretPos, handled: false };

    const formatted = formatDateInput(backspaceResult.value);
    return { value: formatted, caretPos: backspaceResult.caretPos, handled: true };
  }

  return { value, caretPos, handled: false };
};
